import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hasPermission, isRealEventPoster, type FeaturedEventInfo, type ScheduledEvent } from "@tih/shared";
import {
  adminFetchDashboard,
  adminFetchEvent,
  adminFetchEvents,
  adminFetchFeaturedEvent,
} from "../../api/client";
import { AdminIcon, type AdminIconName } from "../../components/admin/AdminIcon";
import { useAuth } from "../../context/AuthContext";
import {
  eventDisplayTitle,
  formatEventWhen,
  phaseBadgeClass,
  phaseLabel,
  rowPhase,
} from "../../lib/event-admin-utils";
import { statsWatchLabel } from "../../lib/statsDisplay";

type HubLink = {
  to: string;
  label: string;
  description: string;
  icon: AdminIconName;
  feature: "check_in" | "attendance" | "events" | "rsvps";
  external?: boolean;
};

const LINKS: HubLink[] = [
  {
    to: "/admin/check-in",
    label: "Check-in desk",
    description: "Door desk for tonight’s guests",
    icon: "checkin",
    feature: "check_in",
  },
  {
    to: "/admin/check-in/self",
    label: "Self check-in kiosk",
    description: "Tablet kiosk for walk-up check-in",
    icon: "kiosk",
    feature: "check_in",
    external: true,
  },
  {
    to: "/admin/attendance",
    label: "Attendance",
    description: "Checked-in report, CSV, and email",
    icon: "attendance",
    feature: "attendance",
  },
  {
    to: "/admin/rsvps",
    label: "All RSVPs",
    description: "Reservations inbox across nights",
    icon: "rsvp",
    feature: "rsvps",
  },
  {
    to: "/admin/events",
    label: "Event calendar",
    description: "Schedule and edit overnight events",
    icon: "events",
    feature: "events",
  },
];

export function AdminTonightPage() {
  const { user } = useAuth();
  const [featuredMeta, setFeaturedMeta] = useState<FeaturedEventInfo | null>(null);
  const [event, setEvent] = useState<ScheduledEvent | null>(null);
  const [watchingNow, setWatchingNow] = useState<number | null>(null);
  const [watchLabel, setWatchLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [featured, events, dash] = await Promise.all([
          adminFetchFeaturedEvent().catch(() => null),
          adminFetchEvents().catch(() => [] as ScheduledEvent[]),
          adminFetchDashboard().catch(() => null),
        ]);
        if (cancelled) return;

        let nextEvent: ScheduledEvent | null = null;
        let nextMeta = featured;
        if (featured) {
          nextEvent = await adminFetchEvent(featured.id).catch(() => null);
        }
        if (!nextEvent && events.length > 0) {
          const now = Date.now();
          const pick =
            events.find((e) => rowPhase(e, now) === "ongoing") ??
            events.find((e) => rowPhase(e, now) === "upcoming") ??
            events[0];
          nextEvent = pick;
          if (!nextMeta && pick) {
            const phase = rowPhase(pick, now);
            if (phase === "ongoing" || phase === "upcoming") {
              nextMeta = { id: pick.id, eventPhase: phase };
            }
          }
        }

        setFeaturedMeta(nextMeta);
        setEvent(nextEvent);
        if (dash?.stats) {
          setWatchingNow(dash.stats.watchingNow);
          setWatchLabel(statsWatchLabel(dash.stats));
        }
      } catch (err) {
        if (!cancelled) setError(String((err as Error)?.message ?? err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleLinks = LINKS.filter((l) => hasPermission(user, l.feature));
  const poster = event && isRealEventPoster(event.posterUrl) ? event.posterUrl : null;
  const title = event ? eventDisplayTitle(event) : null;
  const phase = event ? rowPhase(event) : null;
  const streamHref = event ? `/admin/events/${event.id}/stream` : "/admin/events";
  const canEvents = hasPermission(user, "events");

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Tonight</h1>
        <p>Check-in, attendance, stream, and RSVPs for the featured overnight.</p>
      </header>

      {error && <div className="form-error">{error}</div>}

      {loading ? (
        <p className="admin-hub-empty">Loading tonight’s event…</p>
      ) : event ? (
        <section className="admin-hub-poster-header admin-card">
          {poster ? (
            <img src={poster} alt="" className="admin-hub-poster-thumb" />
          ) : (
            <div className="admin-hub-poster-thumb admin-hub-poster-thumb--empty" aria-hidden />
          )}
          <div className="admin-hub-poster-meta">
            <p className="admin-hub-kicker">Featured overnight</p>
            <h2 className="admin-hub-poster-title">{title}</h2>
            <p className="admin-muted">
              {formatEventWhen(event.startsAt)} → {formatEventWhen(event.endsAt)}
            </p>
            <div className="admin-hub-badge-row">
              {phase && (
                <span className={phaseBadgeClass(phase)}>{phaseLabel(phase)}</span>
              )}
              {featuredMeta?.id === event.id && (
                <span className="admin-badge admin-badge-featured">On public home</span>
              )}
              {watchingNow != null && watchLabel && (
                <span className="admin-hub-watching">
                  <AdminIcon name="watching" size={14} />
                  {watchingNow.toLocaleString()} {watchLabel}
                </span>
              )}
            </div>
            {canEvents && (
              <div className="admin-hub-poster-actions">
                <Link to={streamHref} className="btn btn-primary">
                  <AdminIcon name="stream" size={16} /> Stream tab
                </Link>
                <Link to={`/admin/events/${event.id}/overview`} className="btn btn-ghost">
                  Open event hub
                </Link>
              </div>
            )}
          </div>
        </section>
      ) : (
        <div className="admin-hub-empty admin-card">
          <p>No overnight event is scheduled yet.</p>
          {canEvents ? (
            <Link to="/admin/events/new" className="btn btn-primary">
              Schedule an event
            </Link>
          ) : (
            <p className="admin-muted">Ask an admin with Events access to schedule one.</p>
          )}
        </div>
      )}

      {visibleLinks.length > 0 ? (
        <div className="admin-hub-grid">
          {visibleLinks.map((link) => {
            const className = "admin-hub-card";
            const body = (
              <>
                <span className="admin-hub-card-icon" aria-hidden>
                  <AdminIcon name={link.icon} size={22} />
                </span>
                <span className="admin-hub-card-body">
                  <strong>{link.label}</strong>
                  <span>{link.description}</span>
                </span>
              </>
            );
            if (link.external) {
              return (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={className}
                >
                  {body}
                </a>
              );
            }
            return (
              <Link key={link.to} to={link.to} className={className}>
                {body}
              </Link>
            );
          })}
          {canEvents && event && (
            <Link to={streamHref} className="admin-hub-card">
              <span className="admin-hub-card-icon" aria-hidden>
                <AdminIcon name="stream" size={22} />
              </span>
              <span className="admin-hub-card-body">
                <strong>Featured stream</strong>
                <span>Live link and replay for this overnight</span>
              </span>
            </Link>
          )}
        </div>
      ) : (
        <div className="admin-hub-empty admin-card">
          <p>You don’t have Tonight tools on this account yet.</p>
          <Link to="/admin" className="btn btn-ghost">
            Back to dashboard
          </Link>
        </div>
      )}
    </div>
  );
}
